/* COASTA — desktop mega-menu hover state repair, V7 (2026-11-03)
 * Only this script owns desktop pointer activation. Concept's native
 * details-mega continues to own animations, keyboard, outside-click and ARIA.
 * Works after Sticky Header changes position or scroll direction.
 */
(() => {
  'use strict';
  if (window.__coastaMegaHoverV7) return;
  window.__coastaMegaHoverV7 = true;

  const DESKTOP = window.matchMedia('(min-width: 1024px) and (hover: hover) and (pointer: fine)');
  const MENU = '.header-section .header__menu > ul > li > details[is="details-mega"], .header-section .header__menu > ul > li > details[is="details-dropdown"]';
  const SUMMARY = '.header-section .header__menu > ul > li > details[is="details-mega"] > summary, .header-section .header__menu > ul > li > details[is="details-dropdown"] > summary';
  const TOP_ITEM = '.header-section .header__menu > ul > li';
  const originalTriggers = new WeakMap();
  const bound = new WeakSet();
  const closing = new WeakMap();
  let lastMouse = null;
  let measurementFrame = 0;
  let scrollTimer = 0;

  const menus = () => Array.from(document.querySelectorAll(MENU));
  const eligible = (details) => DESKTOP.matches && originalTriggers.get(details) === 'hover';

  function cancelClose(details) {
    const timer = closing.get(details);
    if (timer) clearTimeout(timer);
    closing.delete(details);
  }

  function updatePlacement() {
    if (measurementFrame) cancelAnimationFrame(measurementFrame);
    measurementFrame = requestAnimationFrame(() => {
      measurementFrame = 0;
      const header = document.querySelector('.header-section .header');
      if (!header) return;
      const rect = header.getBoundingClientRect();
      const bottom = Math.max(0, Math.min(window.innerHeight - 48, Math.round(rect.bottom)));
      document.documentElement.style.setProperty('--coasta-mega-viewport-top', `${bottom}px`);
    });
  }

  function closeOthers(current = null) {
    menus().forEach((menu) => {
      if (menu !== current) {
        cancelClose(menu);
        if (menu.open) menu.open = false;
      }
    });
  }

  function openNow(details) {
    if (!eligible(details) || !details.isConnected) return;
    cancelClose(details);
    if (details.open) return; // Avoid re-running animations on pointermove.
    closeOthers(details);
    document.querySelector('.header-section')?.classList.remove('header-hidden');
    // The same native setter used in the successful Console test. No hover
    // recheck or open delay: sticky-header movement cannot cancel activation.
    details.open = true;
    updatePlacement();
    requestAnimationFrame(updatePlacement);
  }

  function elementUnderPointer() {
    if (!lastMouse) return null;
    return document.elementFromPoint(lastMouse.x, lastMouse.y);
  }

  function insideMenu(details) {
    const node = elementUnderPointer();
    return Boolean(node && details.contains(node));
  }

  function scheduleClose(details) {
    cancelClose(details);
    closing.set(details, setTimeout(() => {
      closing.delete(details);
      if (!details.isConnected) return;
      if (insideMenu(details)) return;
      if (details.open) details.open = false;
    }, 220));
  }

  function setMode(details) {
    if (!originalTriggers.has(details)) {
      originalTriggers.set(details, details.getAttribute('trigger') || 'click');
    }
    const original = originalTriggers.get(details);
    // Disable the native immediate mouseleave only on desktop, otherwise
    // Concept and COASTA both manipulate the same open state.
    if (DESKTOP.matches && original === 'hover') details.setAttribute('trigger', 'click');
    else details.setAttribute('trigger', original);
  }

  function bind(root = document) {
    const list = root.matches?.(MENU) ? [root] : Array.from(root.querySelectorAll?.(MENU) || []);
    list.forEach((details) => {
      setMode(details);
      if (bound.has(details)) return;
      bound.add(details);

      const summary = details.querySelector(':scope > summary');
      if (!summary) return;

      // Preserve COASTA's original behavior: desktop clicking a menu title
      // follows its collection link; hovering opens the menu.
      summary.addEventListener('click', (event) => {
        if (!eligible(details)) return;
        const link = summary.getAttribute('data-link');
        if (!link || link === '#') return;
        event.preventDefault();
        event.stopImmediatePropagation();
        window.location.assign(link);
      }, true);

      // Pointer events are primary. Mouse events are a fallback for browsers
      // where reparented/fixed sticky elements miss pointerenter after scroll.
      summary.addEventListener('pointerenter', (event) => {
        if (event.pointerType === 'mouse') openNow(details);
      });
      summary.addEventListener('mouseenter', () => {
        if (DESKTOP.matches) openNow(details);
      });
      details.addEventListener('pointerenter', (event) => {
        if (event.pointerType === 'mouse') {
          cancelClose(details);
          if (!details.open) openNow(details);
        }
      });
      details.addEventListener('pointerleave', (event) => {
        if (event.pointerType === 'mouse' && eligible(details)) scheduleClose(details);
      });
      details.addEventListener('mouseleave', () => {
        if (eligible(details)) scheduleClose(details);
      });
      details.addEventListener('focusin', () => {
        if (DESKTOP.matches) { cancelClose(details); closeOthers(details); }
      });
      details.addEventListener('toggle', () => {
        if (details.open && DESKTOP.matches) updatePlacement();
      });
    });
  }

  function activateTab(target) {
    const tab = target.closest?.('[data-coasta-mega-tabs] button[role="tab"]');
    if (!tab || tab.disabled) return;
    const tabs = tab.closest('[data-coasta-mega-tabs]');
    const index = Number(tab.dataset.index ?? Array.from(tabs.querySelectorAll('button[role="tab"]')).indexOf(tab));
    if (Number.isInteger(index) && index >= 0 && tabs.selectedIndex !== index) tabs.selectedIndex = index;
  }

  // Delegated capture survives Shopify section re-rendering. In particular,
  // pointermove re-opens a menu when the sticky header moves *under* a cursor
  // without a new pointerenter event.
  function handlePointer(event) {
    if (!DESKTOP.matches || event.pointerType !== 'mouse') return;
    lastMouse = { x: event.clientX, y: event.clientY };
    const target = event.target;
    if (!(target instanceof Element)) return;
    if (event.type === 'pointerover') activateTab(target);

    const summary = target.closest(SUMMARY);
    if (summary) {
      const details = summary.parentElement;
      if (details && eligible(details)) openNow(details);
      return;
    }
    const item = target.closest(TOP_ITEM);
    if (item && !item.querySelector(':scope > details[is="details-mega"], :scope > details[is="details-dropdown"]')) {
      closeOthers();
    }
  }
  document.addEventListener('pointerover', handlePointer, true);
  document.addEventListener('pointermove', handlePointer, { capture: true, passive: true });

  document.addEventListener('pointerdown', (event) => {
    if (!DESKTOP.matches || !(event.target instanceof Node)) return;
    if (!menus().some((details) => details.contains(event.target))) closeOthers();
  }, true);

  // Do not close on EVERY scroll tick (regression in V4–V6). Concept's
  // compact-header transition may close the menu once. After scrolling ends,
  // reopen only when the real mouse cursor is still over a nav summary.
  window.addEventListener('scroll', () => {
    if (!DESKTOP.matches) return;
    if (menus().some((details) => details.open)) updatePlacement();
    clearTimeout(scrollTimer);
    scrollTimer = setTimeout(() => {
      const target = elementUnderPointer();
      const summary = target?.closest?.(SUMMARY);
      if (summary && eligible(summary.parentElement)) openNow(summary.parentElement);
    }, 130);
  }, { passive: true });

  document.addEventListener('header:scrolled', updatePlacement);
  window.addEventListener('resize', updatePlacement, { passive: true });
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') closeOthers();
  });
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) closeOthers();
  });
  window.addEventListener('pagehide', () => closeOthers());
  DESKTOP.addEventListener?.('change', () => {
    closeOthers();
    menus().forEach(setMode);
  });
  const ready = () => { bind(); updatePlacement(); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', ready, { once: true });
  else ready();
  document.addEventListener('shopify:section:load', (event) => bind(event.target));
})();
